import { useStore } from '@/store';
import { selectActiveStudy } from '@/store/selectors';
import { Select } from '@/components/ui/Select';
import { Tooltip } from '@/components/ui/Tooltip';
import { PRESETS } from '@/config/presets';
import {
  CalculationMode as CM,
  StudyType as ST,
  RouteOfAdministration as ROA,
  BcsClass,
} from '@bioavail/shared-types';

/**
 * Study mode & presets bar (always visible at the top of Calculator, P1.5.1).
 * Mode toggle (F-054), study type selector (F-029), preset dropdown (F-056),
 * auto-detect banner (F-006).
 */
export function StudyModeBar() {
  const study = useStore(selectActiveStudy);
  const setMode = useStore((s) => s.setMode);
  const setStudyType = useStore((s) => s.setStudyType);
  const loadPreset = useStore((s) => s.loadPreset);
  const setBcsClass = useStore((s) => s.setBcsClass);
  const bannerDismissed = useStore((s) => s.modeBannerDismissed);
  const dismissBanner = useStore((s) => s.dismissModeBanner);

  if (!study) return null;

  // Auto-detect rule (F-006): IV + non-IV → absolute; all non-IV → relative.
  const routes = study.arms.map((a) => a.route);
  const hasIv = routes.includes(ROA.IV);
  const allNonIv = routes.every((r) => r !== ROA.IV);
  let inferred: CM | null = null;
  if (hasIv && routes.some((r) => r !== ROA.IV)) inferred = CM.Absolute;
  else if (allNonIv && routes.length > 0) inferred = CM.Relative;

  const bannerMismatch = inferred && inferred !== study.mode && !bannerDismissed;

  return (
    <div className="study-mode-bar">
      <div className="study-mode-primary">
        <span className="field-label">
          Mode
          <Tooltip label="Calculation mode" learnMore={{ label: 'See regulatory guidance', screen: 'reference' }}>
            Absolute F requires an IV reference arm and is bounded by 100%. Relative F
            compares two non-IV formulations and may exceed 100%.
          </Tooltip>
        </span>
        <div className="mode-toggle mode-toggle--primary" role="group" aria-label="Calculation mode">
          <button
            type="button"
            className={study.mode === CM.Absolute ? 'active' : ''}
            aria-pressed={study.mode === CM.Absolute}
            onClick={() => setMode(CM.Absolute)}
            title="F = (AUC_test × Dose_ref) / (AUC_ref × Dose_test). Use when one arm is IV."
          >
            Absolute F
          </button>
          <button
            type="button"
            className={study.mode === CM.Relative ? 'active' : ''}
            aria-pressed={study.mode === CM.Relative}
            onClick={() => setMode(CM.Relative)}
            title="F_rel = (AUC_test × Dose_ref) / (AUC_ref × Dose_test). Use for two non-IV formulations."
          >
            Relative F
          </button>
        </div>
      </div>

      <div className="study-mode-secondary">
        <Select
          label="Study type"
          value={study.studyType}
          onChange={(v) => setStudyType(v as ST)}
          options={[
            { value: ST.SingleDose, label: 'Single-dose' },
            { value: ST.SteadyState, label: 'Steady-state', disabled: true },
          ]}
          hint="Steady-state is available in Phase 2 — requires AUC(0-τ)."
        />

        <Select
          label="Preset"
          value=""
          onChange={(v) => {
            if (v) loadPreset(v);
          }}
          options={[
            { value: '', label: 'Choose a preset…' },
            ...PRESETS.map((p) => ({ value: p.id, label: p.name })),
          ]}
        />

        <Select
          label="BCS class"
          value={study.bcsClass ?? ''}
          onChange={(v) => setBcsClass(v === '' ? undefined : (v as BcsClass))}
          options={[
            { value: '', label: '(none)' },
            { value: BcsClass.I, label: 'Class I' },
            { value: BcsClass.II, label: 'Class II' },
            { value: BcsClass.III, label: 'Class III' },
            { value: BcsClass.IV, label: 'Class IV' },
          ]}
          hint="Optional — contextualizes food-effect & solubility interpretation."
        />
      </div>

      {bannerMismatch ? (
        <div className="mode-banner" role="status">
          <span>Auto-detected mode: {inferred === CM.Absolute ? 'Absolute' : 'Relative'}.</span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setMode(inferred!)}>
            Apply
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={dismissBanner}>
            Dismiss
          </button>
        </div>
      ) : null}
    </div>
  );
}
